// src/Components/Modules/Purchase/Vendors.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, IconButton, InputAdornment
} from '@mui/material';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function Vendors() {
    const [vendors, setVendors] = useState([]);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        vendor_name: '', contact_person: '', phone: '', email: '',
        address: '', tin_number: '', trade_license: ''
    });

    const fetchVendors = async () => {
        try {
            const res = await AxiosAuthInstance.get('vendors', { params: { search } });
            setVendors(res.data.vendors || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => { fetchVendors(); }, [search]);

    const openDialog = (vendor = null) => {
        if (vendor) {
            setEditing(vendor);
            setForm({
                vendor_name: vendor.vendor_name,
                contact_person: vendor.contact_person || '',
                phone: vendor.phone || '',
                email: vendor.email || '',
                address: vendor.address || '',
                tin_number: vendor.tin_number || '',
                trade_license: vendor.trade_license || '',
            });
        } else {
            setEditing(null);
            setForm({ vendor_name: '', contact_person: '', phone: '', email: '', address: '', tin_number: '', trade_license: '' });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const url = editing ? `vendors/${editing.id}` : 'vendors';
            const method = editing ? 'put' : 'post';
            await AxiosAuthInstance[method](url, form);
            Swal.fire({ icon: 'success', title: 'Success!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            fetchVendors();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        try {
            await AxiosAuthInstance.post(`vendors/${id}/status/${newStatus}`);
            fetchVendors();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update status' });
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({ title: 'Delete Vendor?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f44336' });
        if (result.isConfirmed) {
            try {
                await AxiosAuthInstance.delete(`vendors/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchVendors();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
            }
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Vendors" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight={700}>Vendor Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField size="small" placeholder="Search name, phone, email..." value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><FaSearch /></InputAdornment> }} />
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => openDialog()}>Add Vendor</Button>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Vendor Name</TableCell>
                            <TableCell>Contact Person</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vendors.map((v) => (
                            <TableRow key={v.id} hover>
                                <TableCell><strong>{v.vendor_name}</strong></TableCell>
                                <TableCell>{v.contact_person || '-'}</TableCell>
                                <TableCell>{v.phone || '-'}</TableCell>
                                <TableCell>{v.email || '-'}</TableCell>
                                <TableCell align="center">
                                    <Chip label={v.status === 1 ? 'Active' : 'Inactive'} 
                                        color={v.status === 1 ? 'success' : 'default'} size="small" />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => handleStatusToggle(v.id, v.status)} 
                                        sx={{ color: v.status === 1 ? '#4caf50' : '#9e9e9e' }}>
                                        {v.status === 1 ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
                                    </IconButton>
                                    <IconButton size="small" onClick={() => openDialog(v)} sx={{ color: '#1976d2' }}><FaEdit /></IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(v.id)} sx={{ color: '#f44336' }}><FaTrash /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Vendor / Company Name *" value={form.vendor_name} onChange={(e) => setForm({...form, vendor_name: e.target.value})} fullWidth required />
                        <TextField label="Contact Person" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Phone *" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} fullWidth required />
                            <TextField label="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} fullWidth />
                        </Box>
                        <TextField label="Address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} fullWidth multiline rows={2} />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="TIN Number" value={form.tin_number} onChange={(e) => setForm({...form, tin_number: e.target.value})} fullWidth />
                            <TextField label="Trade License" value={form.trade_license} onChange={(e) => setForm({...form, trade_license: e.target.value})} fullWidth />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save Vendor</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}